import React, { useEffect, useState, useContext } from 'react';
import { I18nContext } from 'i18n';
import {
  Container,
  Row,
  Col,
  Modal,
  ProgressBar,
  Table,
  OverlayTrigger,
  Tooltip as BTooltip,
} from 'react-bootstrap';
import styled, { css } from 'styled-components';
import { device } from 'utils/device';
import {
  FaCheckCircle,
  FaCloudUploadAlt,
  FaFileExcel,
  FaExclamationTriangle,
  FaTable,
  FaCheck,
  FaTimes,
  FaGrimace,
} from 'react-icons/fa';
import { Button, Alert, Permission, CancelButton, CustomLinks } from 'components';
import { checkPermission } from 'utils/permission';
import { useProgram } from 'hooks/useProgram';
import Dropzone from 'react-dropzone';
import { isEmpty } from 'lodash';
import { useAlert, positions } from 'react-alert'

const IMPORTING = 'Importing...';
const HEADER_ERRORS = [
  'HEADER_ROW_MISSING',
  'EXPECTED_COLUMNS_INCORRECT',
  'COLUMNS_NAMES_NOT_MATCHING',
  'COLUMNS_NOT_ORDERED',
];
const succMessage = 'Scholarship successfully processed';
const allowedPermissions = [
  'canImportScholarships',
  'canEditScholarships',
  'canCreateScholarships',
];
const fileStructure = [
  'program_list_name',
  'upload_organization',
  'upload_selfdescription',
  'upload_award_amount',
  'upload_minimum_award',
  'upload_awards',
  'upload_gpa',
  'program_list_open',
  'program_list_close',
  'program_award_announcement_date',
  'upload_program_url',
  'upload_application_url',
  'program_application_type',
  'program_contest',
  'upload_is_need_based',
  'upload_is_merit_based',
  'upload_nspa_id',
  'upload_minimum_class_rank_percentage',
  'upload_minimum_overall_sat',
  'upload_minimum_act_composite',
  'upload_enrollment_status',
  'upload_maximum_agi',
  'upload_maximum_efc',
];

const UploadScholarshipsModal = ({ show, onHide }) => {
  const { translate } = useContext(I18nContext);
  const {
    dataImport,
    loadingImport,
    executeImport,
    dataImports,
    executeImports,
  } = useProgram();

  const [uploadProgress, setUploadProgress] = useState(0);
  const [checkingProgress, setCheckingProgress] = useState(0);
  const [theFile, setTheFile] = useState();
  const [uploadClicked, setUploadClicked] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [id, setId] = useState();
  const [showErrors, setShowErros] = useState(false);
  const [showStructure, setShowStructure] = useState(false);
  const alert = useAlert()
  const theMessage = (
    <Alert variant="warning">{translate('upload_permission')}</Alert>
  );

  useEffect(() => {
    if (loadingImport) {
      setUploadProgress(10);
    } else if (uploadProgress) {
      setUploadProgress(100);
    }
  }, [loadingImport]);

  useEffect(() => {
    if (uploadProgress && uploadProgress < 80 && loadingImport) {
      setUploadProgress(uploadProgress + 10);
    }
  }, [uploadProgress]);

  useEffect(() => {
    if (dataImports) {
      const { data } = dataImports;

      const checking = data.find((item) => item.id === id);

      if (checkingProgress < 80) {
        setCheckingProgress(checkingProgress + 10);
      }

      if (checking?.message === IMPORTING) {
        //Go again
        setTimeout(() => {
          executeImports();
        }, 1000);
      } else {
        setCheckingProgress(100);
        setTimeout(() => {
          setShowResult(true);
        }, 1000);
      }
    }
  }, [dataImports]);

  useEffect(() => {
    if (dataImport) {
      setId(dataImport.id);
    }
  }, [dataImport]);

  useEffect(() => {
    if (id) {
      executeImports();
    }
  }, [id]);

  const hanldeDrop = (acceptedFiles) => {
    const [acceptedFile] = acceptedFiles;

    const validFileExtensions = ['xlsx', 'csv']
    if (acceptedFile) {
      const ext = acceptedFile.name.split('.').pop();
      if (validFileExtensions.indexOf(ext) >= 0) {
        setTheFile(acceptedFile);
      } else {
        alert.error('Not supported file.', { position: positions.MIDDLE })
      }
    }
  };

  const handleUpload = () => {
    if (!theFile) return;
    const formData = new FormData();
    formData.append('file', theFile);
    executeImport({ data: formData });
    setUploadClicked(true);
  };

  const handleOnShow = () => {
    setUploadClicked(false);
    setCheckingProgress(0);
    setUploadProgress(0);
    setShowResult(false);
    setTheFile();
    setShowErros(false);
    setShowStructure(false);
  };

  const renderProgress = () => (
    <UploadProgress>
      <Row>
        <Col md="2">
          <FileExcel />
        </Col>
        <Col>
          <div>
            <Progress
              variant="success"
              animated={loadingImport}
              now={uploadProgress}
            />
            <ProgressMessage>{translate('upload_uploading')}</ProgressMessage>
          </div>
          <div>
            <Progress
              variant="success"
              animated={checkingProgress < 100}
              now={checkingProgress}
            />
            <ProgressMessage>{translate('upload_checkings')}</ProgressMessage>
          </div>
        </Col>
      </Row>
    </UploadProgress>
  );

  const renderDropZone = () => (
    <Permission permission={allowedPermissions} message={theMessage} every>
      <Dropzone onDrop={hanldeDrop} multiple={false}>
        {({ getRootProps, getInputProps }) => (
          <section>
            <UploadZone {...getRootProps()}>
              <input
                {...getInputProps()}
                accept=".xlsx, .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv"
              />
              <div>
                {theFile ? (
                  <div>
                    <FileExcel />
                    <p>{theFile.name}</p>
                  </div>
                ) : (
                  <FaCloudUploadAlt />
                )}
              </div>
              <p>{translate('upload_drop')}</p>
            </UploadZone>
          </section>
        )}
      </Dropzone>
    </Permission>
  );

  const renderShowErrors = () => {
    return (
      <>
        {!showErrors ? (
          <span onClick={() => setShowErros(true)}>
            {translate('upload_show_details')}
          </span>
        ) : (
          <span onClick={() => setShowErros(false)}>
            {translate('upload_hide_details')}
          </span>
        )}
      </>
    );
  };

  const renderShowFileStructure = () => (
    <>
      {!showStructure ? (
        <span onClick={() => setShowStructure(true)}>
          {translate('upload_show_structure')}
        </span>
      ) : (
        <span onClick={() => setShowStructure(false)}>
          {translate('upload_hide_structure')}
        </span>
      )}
    </>
  );

  const renderRow = (
    { spreadsheetRow, programName, ieLogErrors, messageCode },
    index
  ) => {
    return (
      <React.Fragment key={index}>
        {ieLogErrors.length ? (
          ieLogErrors.map(({ fieldName, fieldValue, messageCode }) => (
            <tr>
              <td>{spreadsheetRow}</td>
              <td>{programName}</td>
              <td>
                <span>{fieldName}</span> <span>{`(${fieldValue})`}</span>
              </td>
              <td>
                <span>{messageCode?.message}</span>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td>{spreadsheetRow}</td>
            <td>{programName}</td>
            <td></td>
            <td>{messageCode?.message}</td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderErrors = (ieLogDetails) => (
    <Row>
      <StyledTable>
        <h5>{translate('upload_result_details')}</h5>
        <Table hover size="sm">
          <thead>
            <RowHeader>
              <th>{translate('upload_result_row')}</th>
              <th>{translate('upload_result_program')}</th>
              <th>{translate('upload_result_value')}</th>
              <th>{translate('upload_result_error')}</th>
            </RowHeader>
          </thead>
          <tbody>
            {ieLogDetails &&
              ieLogDetails.map((details, index) => (
                <>
                  {succMessage !== details.messageCode?.message
                    ? renderRow(details, index)
                    : null}
                </>
              ))}
          </tbody>
        </Table>
      </StyledTable>
    </Row>
  );

  const renderFileStructure = () => (
    <Row>
      <StyledTable>
        <p>{translate('upload_malformed_title')}</p>
        <Table hover size="sm">
          <thead>
            <RowHeader>
              <th>{translate('upload_column_order')}</th>
              <th>{translate('upload_column_name')}</th>
            </RowHeader>
          </thead>
          <tbody>
            {fileStructure.map((item, index) => (
              <tr key={index}>
                <td>{index+1}</td>
                <td>{translate(item)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </StyledTable>
    </Row>
  );

  const renderResult = () => {
    const { data } = dataImports;

    const imports = data.find((item) => item.id === id);
    const {
      filename,
      failedRows,
      message,
      importedRows,
      ignoredRows,
      ieLogDetails,
      messageCode,
    } = imports;

    const uploadError = HEADER_ERRORS.indexOf(messageCode.name) >= 0;
    const uploadWarning = !isEmpty(
      ieLogDetails.find((item) => item.messageCode?.message !== succMessage)
    );

    let variant = uploadError ? 'danger' : 'success';
    if (uploadWarning) {
      variant = 'warning';
    }

    const icons = {
      danger: <Grimace />,
      success: <CheckIcon />,
      warning: <ExclamationTriangle />,
    };

    return (
      <div>
        <Alert variant={variant}>
          <Container> 
            <Row>
              <Summary md="10">
                <h3>{translate(`upload_${variant}`)}</h3>
                {translate(`upload_${variant}_review`)}
              </Summary>
              <Col>{icons[variant]}</Col>
            </Row>
            <Row>
              <Summary>
                <h5>{translate('upload_result_title')}</h5>
                <p>{translate('upload_result_name', { filename })}</p>
                {!uploadError && (
                  <>
                    <p>{translate('upload_result_updated', { importedRows })}</p>
                    {ignoredRows > 0 && <p>{translate('upload_result_ignored', { ignoredRows })}</p>}
                    {failedRows > 0 && (
                      <p>{translate('upload_result_failed', { failedRows })}</p>
                    )}
                  </>
                )}
                <p>
                  {translate(`upload_result_${variant}`, { message })}
                  {ieLogDetails &&
                    uploadWarning &&
                    ieLogDetails.length > 0 &&
                    renderShowErrors()}
                </p>
                {uploadError && <p>{renderShowFileStructure()}</p>}
              </Summary>
            </Row>
            {showErrors && renderErrors(ieLogDetails)}
            {showStructure && renderFileStructure()}
          </Container>
        </Alert>
      </div>
    );
  };

  const renderUpload = () => (
    <Container>
      <DownloadMessage>
        <Col>{uploadClicked ? renderProgress() : renderDropZone()}</Col>
      </DownloadMessage>
      <Tabs>
        <Tab md="2" highlight="true">
          {translate('upload_overview')}
        </Tab>
        <Tab md="2">{translate('upload_options')}</Tab>
      </Tabs>
      <Row>
        <TabContent>{translate('upload_content')}</TabContent>
      </Row>
    </Container>
  );

  const Tooltip = ({ text, children }) => {
    return (
      <OverlayTrigger placement="top" overlay={<BTooltip>{text}</BTooltip>}>
        {children}
      </OverlayTrigger>
    );
  };

  const renderLegend = () => {
    const { data } = dataImports;

    const imports = data.find((item) => item.id === id);
    const { failedRows, totalNumRecords, importedRows } = imports;

    return (
      <Legend>
        <Row>
          <Col>
            <Tooltip text="Total number of records">
              <FaTable />
            </Tooltip>{' '}
            <span>{totalNumRecords}</span>
          </Col>
          <Col>
            <Tooltip text="Imported rows">
              <FaCheck />
            </Tooltip>{' '}
            <span>{importedRows}</span>
          </Col>
          <Col>
            <Tooltip text="Failed rows">
              <FaTimes />
            </Tooltip>{' '}
            <span>{failedRows}</span>
          </Col>
        </Row>
      </Legend>
    );
  };

  return (
    <ModalContainer
      show={show}
      onHide={onHide}
      onShow={() => handleOnShow()}
      backdrop="static"
    >
      <Header closeButton>
        <Modal.Title>
          <ModalTitle>{translate('upload_title')}</ModalTitle>
          <ModalSubtitle>
            <CustomLinks location="UPLOAD.INFORMATION" />
          </ModalSubtitle>
        </Modal.Title>
      </Header>
      <Body>{dataImports && showResult ? renderResult() : renderUpload()}</Body>
      <ModalFooter>
        {!showResult ? (
          <>
            <Button
              primary="true"
              onClick={handleUpload}
              disabled={
                uploadClicked ||
                !checkPermission(allowedPermissions, true) ||
                !theFile
              }
            >
              {translate('upload_upload')}
            </Button>
            <CancelButton onClick={onHide} />
          </>
        ) : (
          <>
            {renderLegend()}
            <Button primary="true" onClick={onHide}>
              {translate('upload_done')}
            </Button>
          </>
        )}
      </ModalFooter>
    </ModalContainer>
  );
};

const Legend = styled(Container)`
  .col {
    text-align: center;
    &:nth-child(1) {
      color: ${({ theme }) => theme.palette.grayscale[1]};
    }

    &:nth-child(2) {
      color: ${({ theme }) => theme.palette.success[0]};
    }

    &:nth-child(3) {
      color: ${({ theme }) => theme.palette.danger[2]};
    }

    > span {
      color: ${({ theme }) => theme.palette.grayscale[0]};
    }
  }
`;

const StyledTable = styled(Col)`
  background-color: ${({ theme }) => theme.palette.grayscale[8]};
  border: 1px solid ${({ theme }) => theme.palette.alert[1]};
  padding: ${({ theme }) => theme.sizes.normal};
  font-size: ${({ theme }) => theme.sizes.small};

  > h5 {
    color: ${({ theme }) => theme.palette.alert[1]};
    font-weight: ${({ theme }) => theme.fonts.weight.bold};
  }

  > table > tbody td {
    padding: 0.5rem;

    &:first-child {
      text-align: center;
    }

    &:last-child {
      white-space: normal;
    }
  }
`;

const RowHeader = styled.tr`
  > th {
    border-top: 0;
    border-right: 2px solid ${({ theme }) => theme.palette.grayscale[6]};
    white-space: nowrap;

    &:first-child {
      text-align: center;
    }

    &:last-child {
      border-right: 0px;
    }
  }
`;

const ExclamationTriangle = styled(FaExclamationTriangle)`
  font-size: 4.5rem;
  color: ${({ theme }) => theme.palette.alert[1]};
`;

const Summary = styled(Col)`
  margin-bottom: ${({ theme }) => theme.sizes.large};
  > h3 {
    font-weight: ${({ theme }) => theme.fonts.weight.bold};
    margin-top: ${({ theme }) => theme.sizes.normal};
  }

  > h5 {
    font-weight: ${({ theme }) => theme.fonts.weight.bold};
  }

  > p {
    line-height: 0.5rem;
    font-weight: ${({ theme }) => theme.fonts.weight.light};
  }
`;

const ProgressMessage = styled.p`
  font-size: ${({ theme }) => theme.sizes.small};
  font-weight: ${({ theme }) => theme.fonts.weight.light};
  margin: 0.5rem;
`;

const Progress = styled(ProgressBar)`
  height: 0.6rem;
`;

const UploadProgress = styled(Container)`
  /* display: flex; */
  margin: 3rem 0;
`;

const ModalFooter = styled(Modal.Footer)`
  border: 0;
  justify-content: center;

  > button.btn-link {
    color: ${({ theme }) => theme.palette.grayscale[4]};
  }
`;

const FileExcel = styled(FaFileExcel)`
  font-size: 3.5rem;
`;

const ModalContainer = styled(Modal)`
  font-size: ${({ theme }) => theme.sizes.normal};
  color: ${({ theme }) => theme.palette.grayscale[2]};

  .modal-dialog {
    max-width: ${({ theme }) => theme.sizes.maxWidth};
  }

  .modal-content {
    border-radius: ${({ theme }) => theme.sizes.small};
    padding: ${({ theme }) => `${theme.sizes.normal} ${theme.sizes.large}`};
    border: 0;
  }

  > div {
    max-width: 700px;
  }

  input,
  textarea {
    font-size: ${({ theme }) => theme.sizes.small};
  }

  .form-row > .col {
    margin-right: 2.5rem;
    @media ${device.mobileL} {
      margin-right: 0;
    }
  }
`;

const UploadZone = styled.div`
  border: 2px dashed ${({ theme }) => theme.palette.grayscale[4]};
  background-color: ${({ theme }) => theme.palette.grayscale[7]};
  height: 10rem;

  > div {
    color: ${({ theme }) => theme.palette.grayscale[5]};
    font-size: 5rem;
    text-align: center;

    > div {
      font-size: 3.5rem;
      > p {
        font-size: ${({ theme }) => theme.sizes.normal};
      }
    }
  }

  > p {
    text-align: center;
    color: ${({ theme }) => theme.palette.grayscale[4]};
    > span {
      color: ${({ theme }) => theme.palette.success[0]};
      text-decoration: underline;
      cursor: pointer;
    }
  }
`;

const Body = styled(Modal.Body)`
  padding: 0;
`;

const Header = styled(Modal.Header)`
  border: 0;

  > .close {
    position: absolute;
    right: ${({ theme }) => theme.sizes.normal};
    top: ${({ theme }) => theme.sizes.normal};
  }

  a {
    color: ${({ theme }) => theme.palette.primary[2]};
  }
`;

const ModalTitle = styled.div`
  font-size: ${({ theme }) => theme.sizes.normal};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
`;

const ModalSubtitle = styled.div`
  font-size: ${({ theme }) => theme.sizes.normal};
  font-weight: ${({ theme }) => theme.fonts.weight.light};
  
  a {
    color: ${({ theme }) => theme.link.color};
  }
`;

const CheckIcon = styled(FaCheckCircle)`
  font-size: 4.5rem;
  color: ${({ theme }) => theme.palette.success[0]};
`;

const Grimace = styled(FaGrimace)`
  font-size: 4.5rem;
  color: ${({ theme }) => theme.palette.danger[0]};
`;

const DownloadMessage = styled(Row)`
  /* margin: 3rem 0; */
`;

const Tabs = styled(Row)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.grayscale[6]};
  margin-top: ${({ theme }) => theme.sizes.large};
`;

const Tab = styled(Col)`
  color: ${({ theme }) => theme.palette.grayscale[6]};
  text-align: center;
  padding: 0.3rem;
  ${(props) =>
    props.highlight &&
    css`
      color: ${({ theme }) => theme.palette.grayscale[2]};
      border-bottom: 3px solid ${({ theme }) => theme.palette.success[0]};
      font-weight: ${({ theme }) => theme.fonts.weight.bold};
    `}
`;

const TabContent = styled(Col)`
  font-size: ${({ theme }) => theme.sizes.small};
  color: ${({ theme }) => theme.palette.grayscale[3]};
  padding: 1rem 0;
`;

export default UploadScholarshipsModal;
